import { Customer } from "@api/types";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { flattenObject, formatDate, formatPhoneNumber } from "@lib/utils";
import { Fragment, useMemo } from "react";

export interface ProfileModalProps {
  profile: Customer;
  open: boolean;
  setOpen: (val: boolean) => void;
}

export function ProfileModal(props: ProfileModalProps) {
  const properties = props.profile.properties;
  const newContact = !props.profile.firstName;
  const flatProperties = useMemo(() => flattenObject(properties), [properties]);
  return (
    <Transition.Root show={props.open} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-20 overflow-hidden" onClose={props.setOpen}>
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="bg-opacity-40 absolute inset-0 transition bg-black" />

          <div className="sm:pl-16 fixed inset-y-0 right-0 flex max-w-full pl-10">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-0"
              enterTo="translate-x-full"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-sm">
                <div className="flex flex-col h-full overflow-y-scroll bg-background shadow-xl">
                  <div>
                    <div className="sm:pb-6 relative pb-1">
                      <button
                        type="button"
                        className="top-2 right-2 absolute text-white rounded-md"
                        onClick={() => props.setOpen(false)}
                      >
                        <span className="sr-only">Close panel</span>
                        <XIcon className="w-6 h-6" aria-hidden="true" />
                      </button>
                      <div className="bg-pattern h-24" />
                      <div className="flow-root px-4 -mt-12">
                        <div>
                          <div className="flex -m-1">
                            <div className="inline-flex overflow-hidden border-4 border-background rounded-full">
                              <div className="text-foreground flex items-center justify-center w-20 h-20 text-3xl bg-blue-100 rounded-full">
                                {newContact
                                  ? "NC"
                                  : `${props.profile.firstName[0]}${props.profile.lastName[0]}`}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-6">
                          <div>
                            <div className="flex items-center">
                              <h3 className="text-xl font-bold text-foreground">
                                {newContact
                                  ? "New Contact"
                                  : `${props.profile.firstName} ${props.profile.lastName}`}
                              </h3>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {formatPhoneNumber(props.profile.phoneNumber)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <dl className="px-4 space-y-4">
                        <div>
                          <dt className="sm:w-40 sm:flex-shrink-0 text-sm font-medium text-muted-foreground">
                            Date added
                          </dt>
                          <dd className="sm:col-span-2 mt-1 text-sm text-foreground">
                            {formatDate(props.profile.createdAt)}
                          </dd>
                        </div>
                        <div>
                          <dt className="sm:w-40 sm:flex-shrink-0 text-sm font-medium text-muted-foreground">
                            Email
                          </dt>
                          <dd className="sm:col-span-2 mt-1 text-sm text-foreground">
                            <p>{props.profile.email}</p>
                          </dd>
                        </div>
                        {properties && (
                          <div className="pt-4 border-t border-border">
                            <h4 className="mb-2 text-sm font-semibold text-foreground">
                              Custom fields
                            </h4>
                            <div className="space-y-4">
                              {Object.keys(flatProperties).map((key, idx) => (
                                <div key={idx}>
                                  <dt className="sm:w-40 sm:flex-shrink-0 text-sm font-medium text-muted-foreground capitalize">
                                    {key}
                                  </dt>
                                  <dd className="sm:col-span-2 mt-1 text-sm text-foreground capitalize">
                                    <p>{flatProperties[key]}</p>
                                  </dd>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
